// src/pages/analytics/ReportsPage.tsx
// /analytics/reports — tabbed: Reports (generate/list/detail/delete) and
// Schedules (CRUD automated report generation). Tab is URL-driven (?tab=…).

import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useI18n } from '@react/hooks';
import { analyticsApi } from '@core/api';
import type { AvailableDataSource } from '@core/types/analytics';
import { useScope } from '@/hooks/useScope';
import { ReportsTab, SchedulesTab } from '@/features/analytics';

type Tab = 'reports' | 'schedules';
const TABS: Tab[] = ['reports', 'schedules'];

export function ReportsPage() {
  const { t } = useI18n();
  const scope = useScope();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dataSources, setDataSources] = useState<AvailableDataSource[]>([]);

  const tab = useMemo<Tab>(() => {
    const v = searchParams.get('tab');
    return (TABS as string[]).includes(v ?? '') ? (v as Tab) : 'reports';
  }, [searchParams]);

  useEffect(() => {
    analyticsApi.ListAvailableDataSources().then((res) => setDataSources(res.data_sources ?? [])).catch(() => undefined);
  }, []);

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        {/* The tab already names this page; the heading stays for screen readers. */}
        <h1 className="sr-only">{t('analytics.reports.title')}</h1>
        <Link to="/analytics" className="ml-auto text-sm text-primary hover:underline">
          {t('analytics.backToDashboards')}
        </Link>
      </div>

      <div role="tablist" aria-label={t('analytics.reports.title')} className="flex flex-wrap gap-1 border-b border-border">
        {TABS.map((v) => (
          <button
            key={v}
            role="tab"
            aria-selected={tab === v}
            onClick={() => setSearchParams({ tab: v })}
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${
              tab === v ? 'border-primary font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t(`analytics.reports.tab.${v}`)}
          </button>
        ))}
      </div>

      {tab === 'reports' && <ReportsTab scope={scope} dataSources={dataSources} />}
      {tab === 'schedules' && <SchedulesTab scope={scope} />}
    </div>
  );
}
